import React from 'react'
import { css } from '@emotion/core'
import { Container, Box, Grid, Checkbox } from '@material-ui/core'
import { Formik } from 'formik'
import * as Yup from 'yup'

import * as utils from '~/utils'
import { Theme } from '~/styles'
import { Heading, Typography, Input, Button, Header, Main, DateWithValidation } from '~/components'
import { handleChange, handleBlur, handleCheckBox } from '~/utils/formik'
import Loader from '~/components/Loader/Loader'
import theme from '~/styles/theme'
import ErrorIcon from '~/assets/icons/material/error.svg?react'

export interface NameAndDobCollectionFormValues {
  firstName: string
  lastName: string
  preferredName: string
  dob: string
  termsAndConditions: boolean
  consentForms?: number[]
}

interface NameAndDobCollectionFormUIProps {
  initialValues: NameAndDobCollectionFormValues
  onSubmit: (values: NameAndDobCollectionFormValues) => void | Promise<void>
  isLoading: boolean
}

export const NameAndDobCollectionFormUI: React.FC<NameAndDobCollectionFormUIProps> = ({
  initialValues,
  onSubmit,
  isLoading,
}) => {
  const nameSchemaShape: Record<string, any> = {
    firstName: utils.firstNameSchema,
    lastName: utils.lastNameSchema,
    preferredName: Yup.string().nullable(),
    termsAndConditions: Yup.bool()
      .oneOf([true], 'You need to accept the terms and conditions')
      .required(),
    dob: utils.dobSchema,
  }
  const validationSchema = Yup.object().shape(nameSchemaShape)

  return isLoading ? (
    <>
      <Header />
      <Loader />
    </>
  ) : (
    <>
      <Header />
      <Container>
        <Box mt={10}>
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12} md={6} lg={5}>
              <Main px={{ xs: 0 }}>
                <Heading>What&apos;s your name?</Heading>
                <Typography variant="subText">
                  Enter your first and last name as they appear on your driver&apos;s license or
                  other ID.
                </Typography>
                <Box mt={3} />
                <Formik<NameAndDobCollectionFormValues>
                  validateOnMount={true}
                  validateOnChange={false}
                  initialValues={initialValues}
                  onSubmit={onSubmit}
                  validationSchema={validationSchema}
                >
                  {formik => {
                    return (
                      <form onSubmit={formik.handleSubmit} css={styles.wrapper}>
                        <Box my={2}>
                          <Input
                            data-cy="firstName"
                            label="Legal first name"
                            name="firstName"
                            required
                            value={formik.values.firstName || ''}
                            error={formik.touched.firstName ? formik.errors.firstName : void 0}
                            autoComplete="given-name"
                            onChange={handleChange(formik, 'firstName')}
                            onBlur={handleBlur(formik, 'firstName')}
                            aria-invalid={!!formik.errors.firstName}
                          />
                        </Box>
                        <Box mb={2}>
                          <Input
                            data-cy="lastName"
                            label="Legal last name"
                            name="lastName"
                            required
                            error={formik.touched.lastName ? formik.errors.lastName : void 0}
                            value={formik.values.lastName || ''}
                            autoComplete="family-name"
                            onChange={handleChange(formik, 'lastName')}
                            onBlur={handleBlur(formik, 'lastName')}
                          />
                        </Box>
                        <Box mb={2}>
                          <DateWithValidation
                            data-cy="dob"
                            values={formik.values}
                            errors={formik.errors}
                            error={formik.touched.dob ? (formik.errors as any).dob : void 0}
                            onChange={handleChange(formik, 'dob')}
                            onBlur={handleBlur(formik, 'dob')}
                          />
                        </Box>
                        <Box mb={2}>
                          <Input
                            data-cy="preferredName"
                            label="Preferred first name (if different)"
                            name="preferredName"
                            error={
                              formik.touched.preferredName ? formik.errors.preferredName : void 0
                            }
                            value={formik.values.preferredName || ''}
                            autoComplete="family-name"
                            onChange={handleChange(formik, 'preferredName')}
                            onBlur={handleBlur(formik, 'preferredName')}
                          />
                        </Box>
                        <Box mt={2}>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                            }}
                            mb={3}
                          >
                            <Checkbox
                              data-cy="termsAndConditions"
                              style={{
                                paddingRight: 10,
                                paddingTop: 0,
                                paddingLeft: 0,
                                margin: 0,
                                color: theme.colors.navy[500],
                              }}
                              checked={formik.values.termsAndConditions}
                              onChange={handleCheckBox(formik, 'termsAndConditions')}
                              value={formik.values.termsAndConditions}
                            />
                            <Typography variant={'subText'} color="navy.500">
                              By continuing, I agree to{' '}
                              <a
                                href="/termsAndConditions"
                                target="_blank"
                                rel="noopener noreferrer"
                                css={styles.pressableText}
                              >
                                Firefly Health&apos;s terms and conditions
                              </a>
                            </Typography>
                          </Box>
                          {formik.touched.termsAndConditions && formik.errors.termsAndConditions ? (
                            <Box
                              sx={{
                                mt: -3,
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                              }}
                            >
                              <ErrorIcon css={styles.errorIcon} />
                              <Box mt={1}>
                                <Typography css={styles.errorText}>
                                  {`You must agree to our terms and conditions`}
                                </Typography>
                              </Box>
                            </Box>
                          ) : null}
                        </Box>
                        <Box my={5}>
                          <Button
                            data-cy="submit"
                            fullWidth
                            type="submit"
                            css={styles.nextButton}
                            disabled={formik.isSubmitting || isLoading}
                          >
                            Next
                          </Button>
                        </Box>
                      </form>
                    )
                  }}
                </Formik>
              </Main>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </>
  )
}

const styles = {
  wrapper: () => css({ width: '100%' }),
  errorText: (theme: Theme) =>
    css({
      fontSize: '1.2rem',
      color: theme.colors.red[700],
    }),
  errorIcon: (theme: Theme) =>
    css({
      height: '13.3px',
      width: '13.3px',
      color: theme.colors.red[700],
      display: 'inline',
      marginTop: '4px',
      marginRight: '5px',
      marginBottom: '-2px',
    }),
  nextButton: (theme: Theme) =>
    css({
      marginTop: '25%',
      [theme.mediaQueries.lg]: {
        position: 'relative',
        marginTop: '1%',
      },
    }),
  pressableText: (theme: Theme) =>
    css({
      display: 'inline',
      textDecorationLine: 'underline',
      color: theme.colors.navy['500'],
      cursor: 'pointer',
    }),
}
